<template>
    <!-- Desglose de la reserva (ficha nueva): aparece al elegir las fechas,
         antes del botón Reservar. Los montos salen de GET simulation. -->
    <div class="fx-sim" aria-live="polite">
        <p v-if="!datesDefined" class="fx-sim-msg">{{ $t("simulator.ask-for-dates") }}</p>

        <p v-else-if="cargando" class="fx-sim-msg">
            <span class="fx-sim-spin" aria-hidden="true"></span>{{ $t("ficha_nueva.sim.calculating") }}
        </p>

        <p v-else-if="!available" class="fx-sim-msg fx-sim-msg--bad">{{ $t("ficha_nueva.sim.not_available") }}</p>

        <p v-else-if="!(total > 0)" class="fx-sim-msg">
            <span class="fx-sim-spin" aria-hidden="true"></span>{{ $t("ficha_nueva.sim.calculating") }}
        </p>

        <template v-else>
            <div class="fx-sim-top">
                <span>{{ $t("ficha_nueva.sim.book_for") }}</span>
                <span class="fx-sim-money fx-sim-money--lg"><b>{{ plan ? fmt(plan.hoy) : dinero(total).num }}</b> <small>{{ plan ? moneda : dinero(total).cur }}</small></span>
            </div>

            <!-- Modelo de hoy: qué se paga, cuándo y a quién, con fechas. -->
            <div v-if="plan" class="fx-sim-plan">
                <section class="fx-sim-blk fx-sim-blk--hoy">
                    <p class="fx-sim-blk-h"><span>{{ $t("ficha_nueva.sim.today_hf") }}</span><b>{{ fmt(plan.hoy) }}</b></p>
                    <ul>
                        <li>
                            <span>{{ plan.mensual ? $t("ficha_nueva.sim.first_month") : $t("ficha_nueva.sim.stay") }}<small v-if="plan.tramoHoy">{{ plan.tramoHoy }}</small></span>
                            <b>{{ fmt(plan.arriendoHoy) }}</b>
                        </li>
                        <li v-if="plan.servicio > 0"><span>{{ $t("ficha_nueva.sim.service") }}</span><b>{{ fmt(plan.servicio) }}</b></li>
                    </ul>
                </section>

                <section v-if="plan.despues.length" class="fx-sim-blk">
                    <p class="fx-sim-blk-h"><span>{{ $t("ficha_nueva.sim.after_host") }}</span><b>{{ fmt(plan.resto) }}</b></p>
                    <ul>
                        <li v-for="f in plan.despues" :key="f.k"><span>{{ f.t }}<small>{{ f.d }}</small></span><b>{{ fmt(f.v) }}</b></li>
                    </ul>
                </section>

                <p v-if="plan.despues.length" class="fx-sim-total">
                    <span>{{ $t("ficha_nueva.sim.total") }}<small>{{ plan.detalleTotal }}</small></span>
                    <b>{{ fmt(plan.total) }}</b>
                </p>

                <p class="fx-sim-fine">
                    <template v-if="plan.despues.length">{{ $t("ficha_nueva.sim.how") }} </template>{{ $t("ficha_nueva.sim.warranty_note") }}
                </p>
            </div>

            <!-- Modelo anterior (estadía / garantía + cargo por servicio): como siempre. -->
            <template v-else>
                <ul class="fx-sim-rows">
                    <li v-for="f in filas" :key="f.k" :class="['fx-sim-row', f.cls]">
                        <span>{{ f.t }}</span>
                        <span class="fx-sim-money"><b>{{ dinero(f.v).num }}</b> <small>{{ dinero(f.v).cur }}</small></span>
                    </li>
                </ul>
            </template>
        </template>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import Vue from "vue";
import { priceConverter } from "@/Utils/Utils";
import { planDePagos } from "@/Utils/planDePagos";

// Monedas que se escriben con "$" delante (el resto va solo con el código)
const SIMBOLOS = { USD: '$', CLP: '$', MXN: '$', COP: '$', ARS: '$', UYU: '$', CAD: '$', AUD: '$', EUR: '€', GBP: '£', BRL: 'R$', PEN: 'S/' };

export default {
	
    name: "Simulator",
    
    data() {
        return {
            cargando: false,
            turno: 0,
        };
    },

    created() {
        // Del alojamiento cargado, no del parámetro de la URL: esta puede venir
        // con el `idcodigo` y la simulación necesita el idalojamiento.
        const cargado = this.accommodation && this.accommodation.idalojamiento;
        this.setAccommodationId(cargado || this.$route.params.idalojamiento);
        // initDate and endDate are set in datePicker component
    },
    
	methods: {
        
        ...mapActions("simulator", [
            'getSimulation',
            'setAccommodationId',
        ]),

        /**
         * Monto convertido a la moneda del visitante, redondeado hacia arriba
         * (mismo filtro que el precio de arriba), separado en número y código
         * para pintar "$387 USD".
         */
        dinero(amount) {
            const txt = Vue.filter('convertPriceToInteger')({ amount: Number(amount) || 0, currency: this.accommodation.moneda }) || '';
            const i = txt.indexOf(' ');
            if (i < 0) return { num: txt, cur: '' };
            const cur = txt.slice(0, i);
            return { num: (SIMBOLOS[cur] || '') + txt.slice(i + 1), cur };
        },

        /**
         * Monto en la moneda del visitante, entero y redondeado hacia arriba
         * (igual que dinero()). El plan suma estos enteros, así lo que se ve
         * cierra: hoy = primer mes + cargo, total = hoy + después.
         */
        entero(amount) {
            const c = priceConverter({ amount: Number(amount) || 0, currency: this.accommodation.moneda });
            return Math.ceil(Number(c.amount) || 0);
        },

        // Entero ya convertido → "€452" (el código de moneda va aparte).
        fmt(n) {
            const cur = this.moneda;
            const num = cur === 'CLP'
                ? String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.')
                : String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            return (SIMBOLOS[cur] || '') + num;
        },

        // "16 oct" en el idioma de la página.
        fechaCorta(iso) {
            if (!iso) return '';
            const [y, m, d] = iso.split('-').map(Number);
            return new Date(y, m - 1, d, 12).toLocaleDateString(this.$i18n.locale, { day: 'numeric', month: 'short' }).replace('.', '');
        },
        tramo(desde, hasta) {
            return desde && hasta ? this.$t('ficha_nueva.sim.range', { from: this.fechaCorta(desde), to: this.fechaCorta(hasta) }) : '';
        },
        // Precio por noche aproximado ("≈ €15"): solo para explicar las noches sueltas.
        porNocheTxt(amount) {
            const c = priceConverter({ amount: Number(amount) || 0, currency: this.accommodation.moneda });
            return '≈ ' + this.fmt(Math.round(Number(c.amount) || 0));
        },

        async updateSimulation() {
            if (!this.datesDefined) return;
            const turno = ++this.turno;
            this.cargando = true;
            try {
                await this.getSimulation();
            } catch (e) {
                console.error('No se pudo calcular la simulación', e);
            } finally {
                // Solo el último pedido apaga el "Calculando…"
                if (turno === this.turno) this.cargando = false;
            }
        },
        
	},
    
	computed: {
        ...mapState('simulator', [
            'available',
        ]),

        ...mapState("accommodation", [
            'accommodation',
        ]),
        
        ...mapState("simulator", [
            'accommodationId',
            'initDate',
            'endDate',
            'simulation'
        ]),

        ...mapState("exchange", [
            'visitorCurrency',
        ]),

        // Lo que se paga por la plataforma al reservar: el primer mes de
        // arriendo (o la estadía completa si es de menos de 30 días) más la
        // comisión por el servicio de House & Flats.
        total() {
            return Number(this.simulation.total) || 0;
        },

        commissionRate() {
            return this.simulation.commissionRate || 4;
        },

        /**
         * ¿El alojamiento se cobra con el modelo de hoy o con el anterior
         * (estadía / garantía + cargo por servicio)? Lo define el backend
         * mirando al dueño del alojamiento.
         */
        hfCommissionEnabled() {
            return this.simulation.hfCommissionEnabled !== false;
        },

        // Código de la moneda en la que se muestra (la del visitante).
        moneda() {
            return priceConverter({ amount: 1, currency: this.accommodation.moneda }).currency || '';
        },

        /**
         * Plan de pagos (modelo de hoy) ya en enteros de la moneda del visitante.
         * null con el modelo anterior (ahí se usan las filas de siempre).
         */
        plan() {
            if (!this.hfCommissionEnabled) return null;
            const s = this.simulation;
            const n = v => Number(v) || 0;
            const noches = n(s.totalDays);
            const mensual = noches >= 30;
            const base = planDePagos({
                noches, llegada: this.initDate, mensual: n(s.monthlyRent),
                primerMes: n(s.firstMonth) || n(s.stay), restoAnfitrion: n(s.remainingToHost),
            });

            const hoy = this.entero(this.total);
            const arriendoHoy = Math.min(hoy, this.entero(base.hoy.monto));
            const servicio = n(s.commission) > 0 ? hoy - arriendoHoy : 0;
            const resto = n(s.remainingToHost) > 0 ? Math.max(0, this.entero(s.stayTotal) - arriendoHoy) : 0;

            // Filas de "después": cada una en entero; la última cierra contra el resto.
            const filas = base.despues.map(t => ({ ...t, v: this.entero(t.monto) }));
            if (filas.length) filas[filas.length - 1].v = Math.max(0, resto - filas.slice(0, -1).reduce((a, f) => a + f.v, 0));
            const meses = filas.filter(f => f.tipo === 'mes');
            const noche = filas.find(f => f.tipo === 'noches');
            const despues = [];
            if (meses.length >= 3) {
                // Muchos meses: una sola fila "Meses 2 a 6 · €452 cada mes".
                const ultimo = meses[meses.length - 1];
                despues.push({
                    k: 'meses', t: this.$t('ficha_nueva.sim.months_range', { a: meses[0].n, b: ultimo.n }),
                    d: this.$t('ficha_nueva.sim.each_month', { price: this.fmt(meses[0].v) }) + ' · ' + this.tramo(meses[0].desde, ultimo.hasta),
                    v: meses.reduce((a, f) => a + f.v, 0),
                });
            } else {
                meses.forEach(f => despues.push({ k: 'mes' + f.n, t: this.$t('ficha_nueva.pay.month_n', { n: f.n }), d: this.tramo(f.desde, f.hasta), v: f.v }));
            }
            if (noche) {
                despues.push({
                    k: 'noches', t: this.$tc('ficha_nueva.sim.extra_nights', noche.noches, { n: noche.noches }),
                    d: this.tramo(noche.desde, noche.hasta) + ' · ' + this.$t('ficha_nueva.sim.per_night', { price: this.porNocheTxt(base.porNoche) }),
                    v: noche.v,
                });
            }

            return {
                mensual,
                hoy, arriendoHoy, servicio, resto, despues,
                total: hoy + resto,
                tramoHoy: [this.tramo(base.hoy.desde, base.hoy.hasta), this.$tc('ficha_nueva.nights', base.hoy.noches, { n: base.hoy.noches })].filter(Boolean).join(' · '),
                detalleTotal: this.$t('ficha_nueva.sim.total_detail', {
                    nights: this.$tc('ficha_nueva.nights', noches, { n: noches }),
                    rent: this.fmt(arriendoHoy + resto), fee: this.fmt(servicio),
                }),
            };
        },

        /**
         * Filas del desglose. Modelo de hoy: estadía total, lo que se paga hoy
         * (primer mes —o la estadía si es de menos de 30 días— + cargo por
         * servicio) y lo que queda para el anfitrión. Modelo anterior: lo que
         * se paga hoy es estadía o garantía + cargo por servicio.
         */
        filas() {
            const s = this.simulation;
            const n = v => Number(v) || 0;
            const out = [];
            const noches = n(s.totalDays);

            if (n(s.stayTotal) > 0) {
                const t = this.$t('ficha_nueva.sim.stay_total') + (noches > 0 ? ' · ' + this.$tc('ficha_nueva.nights', noches, { n: noches }) : '');
                out.push({ k: 'stay_total', t, v: s.stayTotal });
            }

            out.push({ k: 'today', t: this.$t('ficha_nueva.sim.pay_today'), v: this.total, cls: 'fx-sim-row--today' });

            if (this.hfCommissionEnabled) {
                const base = n(s.firstMonth) || n(s.stay);
                if (base > 0) out.push({ k: 'base', t: '· ' + this.$t(n(s.months) > 0 ? 'ficha_nueva.sim.first_month' : 'ficha_nueva.sim.stay'), v: base, cls: 'fx-sim-row--sub' });
            } else {
                if (n(s.stay) > 0) out.push({ k: 'stay', t: '· ' + this.$t('ficha_nueva.sim.stay'), v: s.stay, cls: 'fx-sim-row--sub' });
                if (n(s.warranty) > 0) out.push({ k: 'warranty', t: '· ' + this.$t('ficha_nueva.sim.warranty'), v: s.warranty, cls: 'fx-sim-row--sub' });
            }
            if (n(s.commission) > 0) out.push({ k: 'commission', t: '· ' + this.$t('ficha_nueva.sim.service'), v: s.commission, cls: 'fx-sim-row--sub' });

            if (this.hfCommissionEnabled && n(s.remainingToHost) > 0) {
                out.push({ k: 'host', t: this.$t('ficha_nueva.sim.to_host'), v: s.remainingToHost });
            }
            return out;
        },

        datesDefined() {
            // Consider dates defined only when they are neither null nor empty strings
            return this.initDate != null && this.initDate !== '' && this.endDate != null && this.endDate !== '';
        },
        
	},
    
    watch: {
        'accommodationId': function(newVal, oldVal) {
            if (this.datesDefined) {
                this.updateSimulation();
            }
        },
        'initDate': function(newVal, oldVal) {
            if (newVal && newVal < this.endDate) {
                this.updateSimulation();
            }
        },
        'endDate': function(newVal, oldVal) {
            if (newVal && newVal > this.initDate) {
                this.updateSimulation();
            }
        },
        'visitorCurrency': function(newVal, oldVal) {
            if (this.datesDefined) {
                this.updateSimulation();
            }
        },
    },
};
</script>

<!-- Estilos: components/DetalleAlojamiento/ficha/ficha.css (.fx-sim) -->